import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Camera, Trash2 } from 'lucide-react';
import { Button } from '../../common';
import { logoutCurrentSession } from '@auw/api-client';
import { logoutActiveAccount } from '@auw/api-client';
import { getActiveAccountId } from '@auw/api-client';
import { useUserStore } from '@auw/stores';
import { clearQuerySessionStorage } from '@auw/workspace-utils';
import { clearWithdrawnAccountClientState, resetRuntimeSessionState } from '@auw/stores';
import { useFeatureFlags } from '@auw/feature-flags';
import { ImageCropModal } from '../components/ImageCropModal';
import { JobRoleInterestsSection } from '../components/JobRoleInterestsSection';
import { LanguageSection } from '../components/LanguageSection';
import { ThemeSection } from '../components/ThemeSection';
import { avatarColor, avatarInitial } from '../utils';
import {
  ActionRow,
  AvatarFallback,
  AvatarImage,
  AvatarLarge,
  AvatarOverlay,
  DeleteConfirmBox,
  DeleteMsg,
  Divider,
  ErrorMsg,
  FieldLabel,
  FieldRow,
  FieldValue,
  PicActionBtn,
  PicActions,
  ProfilePicRow,
  SectionBlock,
  SectionHeader,
  SectionTitle,
} from '../styles';

const ProfileSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;

  > ${SectionBlock} {
    margin-bottom: 0;
  }
`;

const SectionAnchor = styled.div`
  > ${SectionBlock} {
    margin-bottom: 0;
  }
`;

interface ProfileTabProps {
  focusSection?: string;
  focusRequestId?: number;
}

export const ProfileTab: React.FC<ProfileTabProps> = ({ focusSection, focusRequestId }) => {
  const { t } = useTranslation(['settings', 'common']);
  const queryClient = useQueryClient();
  const user = useUserStore((s) => s.user);
  const updateProfile = useUserStore((s) => s.updateProfile);
  const deleteAccount = useUserStore((s) => s.deleteAccount);

  /* P11-5c: tenant policy gates inside the profile tab. */
  const featureFlags = useFeatureFlags();
  const photoEnabled = featureFlags.isEnabled('settings.profile.photo');
  const deleteAccountEnabled = featureFlags.isEnabled('settings.profile.deleteAccount');
  const localeEnabled = featureFlags.isEnabled('settings.locale');
  const personalizationEnabled = featureFlags.isEnabled('settings.personalization');
  /* The "직군 · 관심사 · 피드" section is the user-facing input form
   * that drives daily feed generation (job role + interests +
   * delivery hour). It is meaningless on tenants that don't ship the
   * feed mode at all — the saved values would never be consumed and
   * the inline 피드 시간 카드 would dead-end.  We therefore gate the
   * whole section behind `mode.feed` in addition to the existing
   * `settings.personalization` toggle, which still wins (so a tenant
   * that ships feeds but wants to suppress the personalization
   * surface can do so independently). */
  const feedModeEnabled = featureFlags.isEnabled('mode.feed');

  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [profileAvatarError, setProfileAvatarError] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const interestsSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setProfileAvatarError(false);
  }, [user?.picture]);

  useEffect(() => {
    if (focusSection !== 'interests') return;
    const node = interestsSectionRef.current;
    if (!node) return;
    const timer = window.setTimeout(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [focusRequestId, focusSection]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result as string);
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  const handleCropConfirm = useCallback(
    async (dataUrl: string) => {
      setCropSrc(null);
      try {
        setError(null);
        await updateProfile({ picture: dataUrl });
      } catch {
        setError(t('settings:profile.photoUpdateFail'));
      }
    },
    [updateProfile],
  );

  const handleDeletePicture = useCallback(async () => {
    try {
      setError(null);
      await updateProfile({ picture: '' });
    } catch {
      setError(t('settings:profile.photoDeleteFail'));
    }
  }, [updateProfile]);

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setError(null);
    try {
      const accountId = getActiveAccountId();
      await deleteAccount();
      await logoutCurrentSession().catch(() => {
        // 탈퇴 직후 서버 세션 정리 실패는 로컬 정리를 막지 않음
      });
      logoutActiveAccount();
      clearWithdrawnAccountClientState(accountId);
      resetRuntimeSessionState({ resetDarkMode: true });
      queryClient.clear();
      clearQuerySessionStorage();
      // 탈퇴 후에는 로그아웃과 같이 `/`(랜딩 겸 로그인 페이지)로 전체 리로드.
      window.location.href = '/';
    } catch (e) {
      if (e instanceof Error && e.message.trim()) setError(e.message);
      else setError(t('settings:profile.deleteAccountFail'));
      setDeleting(false);
    }
  };

  const fallback = avatarInitial(user?.name, user?.email);
  const avatarBg = avatarColor(user?.email || user?.name || 'U');

  return (
    <>
      <ImageCropModal
        open={!!cropSrc}
        imageSrc={cropSrc || ''}
        onClose={() => setCropSrc(null)}
        onConfirm={handleCropConfirm}
      />

      <ProfileSections>
        <SectionBlock>
          <SectionHeader>
            <SectionTitle>{t('settings:profile.myInfo')}</SectionTitle>
          </SectionHeader>
          <Divider />

          <ProfilePicRow>
            <AvatarLarge $bg={avatarBg}>
              {user?.picture && !profileAvatarError ? (
                <AvatarImage
                  src={user.picture}
                  alt="profile"
                  referrerPolicy="no-referrer"
                  onError={() => setProfileAvatarError(true)}
                />
              ) : (
                <AvatarFallback>{fallback}</AvatarFallback>
              )}
              {photoEnabled && (
                <AvatarOverlay onClick={() => fileRef.current?.click()}>
                  <Camera size={16} />
                </AvatarOverlay>
              )}
            </AvatarLarge>
            {photoEnabled && (
              <PicActions>
                <PicActionBtn onClick={() => fileRef.current?.click()}>{t('settings:profile.changePhoto')}</PicActionBtn>
                {user?.picture && user?.hasCustomPicture && !profileAvatarError && (
                  <PicActionBtn $danger onClick={handleDeletePicture}>{t('settings:profile.deletePhoto')}</PicActionBtn>
                )}
              </PicActions>
            )}
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFileSelect} />
          </ProfilePicRow>

          <FieldRow>
            <FieldLabel>{t('settings:profile.userId')}</FieldLabel>
            <FieldValue>{user?.email || '-'}</FieldValue>
          </FieldRow>
          <FieldRow>
            <FieldLabel>{t('settings:profile.name')}</FieldLabel>
            <FieldValue>{user?.name || '-'}</FieldValue>
          </FieldRow>

          {error && <ErrorMsg>{error}</ErrorMsg>}
        </SectionBlock>

        {localeEnabled && <LanguageSection />}

        {personalizationEnabled && feedModeEnabled && (
          <SectionAnchor ref={interestsSectionRef}>
            <JobRoleInterestsSection />
          </SectionAnchor>
        )}

        <ThemeSection />

        {deleteAccountEnabled && (
          <SectionBlock>
            <SectionHeader>
              <SectionTitle>{t('settings:profile.accountManage')}</SectionTitle>
            </SectionHeader>
            <Divider />

            {!deleteConfirm ? (
              <ActionRow>
                <Button variant="danger" size="sm" leftIcon={<Trash2 size={14} />} onClick={() => setDeleteConfirm(true)}>
                  {t('settings:profile.deleteAccount')}
                </Button>
              </ActionRow>
            ) : (
              <DeleteConfirmBox>
                <DeleteMsg>{t('settings:profile.deleteAccountConfirm')}</DeleteMsg>
                <ActionRow>
                  <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(false)} disabled={deleting}>{t('common:button.cancel')}</Button>
                  <Button variant="danger" size="sm" onClick={handleDeleteAccount} disabled={deleting}>
                    {deleting ? t('settings:profile.deleteAccountProcessing') : t('settings:profile.deleteAccountButton')}
                  </Button>
                </ActionRow>
              </DeleteConfirmBox>
            )}
          </SectionBlock>
        )}
      </ProfileSections>
    </>
  );
};
